import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Fade,
  Chip,
  Alert,
  Divider,
  Autocomplete,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
  Modal,
  IconButton
} from "@mui/material";
import {
  Assignment as RequestIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  CalendarToday as CalendarIcon,
  ContactPage as ContactIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled from 'styled-components';
import { apiCall } from "../../services/ApiCall";
import { useNavigate } from "react-router-dom";
import config from "../../config/config.json";

// Styled Components with #0d6efd theme
const StyledContainer = styled(Box)`
  
  min-height: 100vh;
  padding: 24px;
`;

const HeaderCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(243, 243, 243, 1) !important;
  border: 1px solid rgba(13, 110, 253, 0.1);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;

  }
`;

const FormCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  box-shadow: 0 8px 32px rgba(243, 243, 243, 1) !important;
  border: 1px solid rgba(13, 110, 253, 0.08);
  margin-bottom: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(243, 243, 243, 1) !important;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1);
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const StyledFormControl = styled(FormControl)`
  .MuiOutlinedInput-root {
    border-radius: 12px !important;
    background: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease !important;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1);
    }
  }
  
  .MuiInputLabel-root {
    font-weight: 500;
    
    &.Mui-focused {
      color: #0d6efd;
    }
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%) !important;
  border-radius: 12px !important;
  padding: 16px 48px !important;
  font-weight: 700 !important;
  font-size: 16px !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(243, 243, 243, 1) !important;
    background: linear-gradient(135deg, #0a58ca 0%, #084298 100%) !important;
  }
  
  &:disabled {
    background: rgba(13, 110, 253, 0.3) !important;
    transform: none !important;
    box-shadow: none !important;
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  width: 100%;
  
  .date-input {
    width: 100%;
    padding: 16.5px 14px;
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    font-size: 16px;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    transition: all 0.3s ease;
    color: #1a202c;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
      box-shadow: 0 4px 12px rgba(243, 243, 243, 1);
      border-color: rgba(0, 0, 0, 0.87);
    }
    
    &:focus {
      outline: none;
      border-color: #0d6efd;
      box-shadow: 0 0 0 2px rgba(243, 243, 243, 1);
      background: rgba(255, 255, 255, 1);
    }
    
    /* Custom calendar icon styling */
    &::-webkit-calendar-picker-indicator {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%230d6efd'%3e%3cpath d='M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z'/%3e%3c/svg%3e");
      background-size: 20px 20px;
      background-repeat: no-repeat;
      background-position: center;
      width: 20px;
      height: 20px;
      cursor: pointer;
      opacity: 0.7;
      transition: opacity 0.3s ease;
    }
    
    &::-webkit-calendar-picker-indicator:hover {
      opacity: 1;
    }
    
    /* Firefox */
    &::-moz-calendar-picker-indicator {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%230d6efd'%3e%3cpath d='M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z'/%3e%3c/svg%3e");
      background-size: 20px 20px;
      background-repeat: no-repeat;
      background-position: center;
      width: 20px;
      height: 20px;
      cursor: pointer;
      opacity: 0.7;
    }
  }
`;

const AddRequest = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Original state variables - keeping exactly the same
  const [toggle, setToggle] = useState(false);
  const [companyData, setCompanyData] = useState([]);
  const [companyUserData, setCompanyUserData] = useState([]);
  const [siteData, setSiteData] = useState([]);
  const [contactDetails, setContactDetails] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({ companyId: "", siteId: "" });
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedContact, setSelectedContact] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Original validation functions - keeping exactly the same
  const validateCompany = (value) => (!value ? "Company is required" : "");
  const validateSite = (value) => (!value || !value.trim() ? "Site is required" : "");

  // Original toggle handler - keeping exactly the same
  const handleToggle = () => {
    setToggle(!toggle);
  };

  // Original API functions - keeping exactly the same
  const getCompanyContact = async (companyId) => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyContact`,
      {},
      { companyId },
      "GET"
    );
    if (isSuccess) {
      const users = data?.data?.comapnyUserData || [];
      setCompanyUserData(users);
      if (users.length) {
        setSelectedContact(users[0]?.id);
        setContactDetails({
          name: users[0].name,
          email: users[0].email,
          phone: users[0].phone,
        });
      }
    }
  };

  const getSitesList = async (companyId) => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getSitesList`,
      {},
      { companyId },
      "GET"
    );
    if (isSuccess) {
      setSiteData(data?.data?.reverse());
      setSelectedSite(data?.data[0]?.id);
    }
  };

  const getCompanyList = async () => {
    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}getCompanyList`,
      {},
      {},
      "GET"
    );
    if (isSuccess) {
      setCompanyData(data?.data?.reverse());
    }
  };

  // Original change handlers - keeping exactly the same logic
  const changeCompany = (selectedOption) => {
    getSitesList(selectedOption ? Number(selectedOption.value) : '');
    setSelectedCompany(selectedOption ? Number(selectedOption.value) : '');
    getCompanyContact(selectedOption ? Number(selectedOption.value) : '');
    const error = validateCompany(selectedOption ? Number(selectedOption.value) : '');
    setErrors((prevErrors) => ({ ...prevErrors, companyId: error }));
  };

  const changeSite = (e) => {
    const value = e.target.value;
    setSelectedSite(value);
    const error = validateSite(value);
    setErrors((prevErrors) => ({ ...prevErrors, siteId: error }));
  };

  const changeContact = (e) => {
    const value = e.target.value;
    setSelectedContact(value);
    const user = companyUserData.find(user => user.id === value);
    if (user) {
      setContactDetails({
        name: user.name,
        email: user.email,
        phone: user.phone,
      });
    }
  };

  // Original date handler - keeping exactly the same
  const handleDateChange = (e) => {
    setScheduledDate(e.target.value);
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Original save handler - keeping exactly the same
  const handleSave = async () => {
    if (!selectedCompany || !selectedSite || !selectedContact || !scheduledDate) {
      return;
    }
    setIsSaving(true);
    const payload = {
      companyId: Number(selectedCompany),
      siteId: Number(selectedSite),
      contactId: Number(selectedContact),
      date: scheduledDate,
    };

    const { isSuccess, data } = await apiCall(
      `${config.POST_LOGIN_API_URL}addNewRequest`,
      {},
      payload,
      "POST"
    );

    if (isSuccess) {
      navigate("/new-request");
    }
    setIsSaving(false);
  };

  // Original useEffect - keeping exactly the same
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getCompanyList();
    }
  }, []);

  // Original company options - keeping exactly the same
  const companyOptions = companyData.map((company) => ({
    value: Number(company.id),
    label: company.name
  }));

  return (
    <StyledContainer>
      {/* Header Section */}
      <HeaderCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'black',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}
              >
                Create New Request
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                Schedule a new inspection request with all necessary details
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Request Information Card */}
      <Fade in timeout={500}>
        <FormCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CalendarIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                Request Information
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: alpha('#0d6efd', 0.1) }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                    Due Date
                  </Typography>
                  <DateInputWrapper>
                    <input
                      type="date"
                      className="date-input"
                      value={scheduledDate}
                      onChange={handleDateChange}
                      min={getTodayDate()}
                    />
                  </DateInputWrapper>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </FormCard>
      </Fade>

      {/* Location Card */}
      <Fade in timeout={700}>
        <FormCard>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LocationIcon sx={{ color: '#0d6efd', mr: 2, fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c' }}>
                Location
              </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: alpha('#0d6efd', 0.1) }} />

            <Grid container spacing={3}>
              {/* Company Selection */}
           
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 500,
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                    Company
                  </Typography>
                  <Autocomplete
                    options={companyOptions}
                    getOptionLabel={(option) => option.label || ''}
                    value={companyOptions.find(option => option.value === selectedCompany) || null}
                    onChange={(event, newValue) => {
                      console.log("selected option", newValue);
                      changeCompany(newValue);
                    }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type to search company..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 1)',
                              boxShadow: '0 4px 12px rgba(243, 243, 243, 1)',
                            },
                            '&.Mui-focused': {
                              background: 'rgba(255, 255, 255, 1)',
                              boxShadow: '0 0 0 2px rgba(13, 110, 253, 0.2)',
                            },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon sx={{ color: '#0d6efd', fontSize: 18 }} />
                          {option.label}
                        </Box>
                      </li>
                    )}
                    noOptionsText="No companies found"
                    clearOnEscape
                    sx={{
                      '& .MuiAutocomplete-popupIndicator': {
                        color: '#64748b',
                      },
                      '& .MuiAutocomplete-clearIndicator': {
                        color: '#64748b',
                      },
                    }}
                  />
                  {errors.companyId && (
                    <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 2 }}>
                      {errors.companyId}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Site Selection */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                    Site
                  </Typography>
                  <StyledFormControl fullWidth>
                    <Select
                      value={selectedSite}
                      onChange={changeSite}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value) return 'Select a site';
                        const site = siteData.find(s => s.id === value);
                        return site ? `${site.address} ${site.city} ${site.state}` : 'Select a site';
                      }}
                      startAdornment={
                        <LocationIcon sx={{ color: '#64748b', mr: 1 }} />
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a site
                      </MenuItem>
                      {siteData.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.address + ' ' + site.city + ' ' + site.state}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.siteId && (
                      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 2 }}>
                        {errors.siteId}
                      </Typography>
                    )}
                  </StyledFormControl>
                </Box>
              </Grid>

              {/* Contact Selection */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                    Select Main Contact
                  </Typography>
                  <StyledFormControl fullWidth>
                    <Select
                      value={selectedContact}
                      onChange={changeContact}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value) return 'Select a contact';
                        const contact = companyUserData.find(user => user.id === value);
                        return contact ? contact.name : 'Select a contact';
                      }}
                      startAdornment={
                        <PersonIcon sx={{ color: '#64748b', mr: 1 }} />
                      }
                    >
                      <MenuItem value="" disabled>
                        Select a contact
                      </MenuItem>
                      {companyUserData.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Box>
              </Grid>

              {/* Contact Details - Row 2 */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                    Name
                  </Typography>
                  <StyledTextField
                    fullWidth
                    placeholder="Name"
                    value={contactDetails?.name}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <PersonIcon sx={{ color: '#64748b', mr: 1 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha('#4caf50', 0.05),
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                    Email Id
                  </Typography>
                  <StyledTextField
                    fullWidth
                    placeholder="Email Id"
                    value={contactDetails?.email}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <EmailIcon sx={{ color: '#64748b', mr: 1 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha('#4caf50', 0.05),
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, ml: 1 }}>
                    Phone Number
                  </Typography>
                  <StyledTextField
                    fullWidth
                    placeholder="Phone Number"
                    value={contactDetails?.phone}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <PhoneIcon sx={{ color: '#64748b', mr: 1 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: alpha('#4caf50', 0.05),
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </FormCard>
      </Fade>

      {/* Action Section */}
      <Box>
  {/* Email Notifications Section */}
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
        Email Notifications
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b' }}>
        Schedule a new inspection request with all necessary details
      </Typography>
    </Box>
    
    <Switch
      checked={!toggle}
      onChange={handleToggle}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: '#0d6efd',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: '#0d6efd',
        },
      }}
    />
  </Box>

  {/* Action Buttons */}
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
    <Button
      variant="outlined"
      sx={{
        color: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
          borderColor: '#0d6efd',
          backgroundColor: 'rgba(13, 110, 253, 0.04)',
        },
      }}
    >
      Cancel
    </Button>
    
    <Button
      variant="contained"
      onClick={handleSave}
      disabled={isSaving}
      sx={{
        backgroundColor: '#0d6efd',
        '&:hover': {
          backgroundColor: '#0b5ed7',
        },
        opacity: isSaving ? 0.6 : 1,
        cursor: isSaving ? "not-allowed" : "pointer",
      }}
    >
      {isSaving ? 'Creating...' : 'Create Request'}
    </Button>
  </Box>
</Box>
    </StyledContainer>
  );
};

export default AddRequest;